import { ShieldCheck } from "lucide-react";
import LegalPage, {
  BulletList,
  DataBlocks,
  Paragraph,
  Strong,
  TextLink,
  type LegalSection,
} from "../../components/LegalPage";
import { COMPANY, CONTACT_PATH } from "../../constants/company.constants";

const sections: LegalSection[] = [
  {
    id: "introduction",
    title: "Introduction",
    content: (
      <>
        <Paragraph>
          This Privacy Policy describes how <Strong>HRx</Strong>, an intelligent
          SaaS-based Human Resource Management System by {COMPANY.name},
          collects, uses, and safeguards the personal and professional data
          processed within its ecosystem.
        </Paragraph>
        <Paragraph>
          By integrating Attendance, Leave, Payroll, and Employee Profiling
          modules, HRx acts as a single source of truth for organizational data
          while ensuring transparency and security.
        </Paragraph>
      </>
    ),
  },
  {
    id: "info-collect",
    title: "Information We Collect",
    content: (
      <>
        <Paragraph>
          HRx collects various types of data to automate HR workflows and
          maintain accurate employment records:
        </Paragraph>
        <DataBlocks
          items={[
            {
              title: "Employee Profiling Data",
              rows: [
                {
                  label: "Includes",
                  value:
                    "Full name, employee ID, designation, department, joining date, employment type, and contact details.",
                },
              ],
            },
            {
              title: "Attendance & Identity Data",
              rows: [
                {
                  label: "Includes",
                  value:
                    "AI-based facial recognition data, biometric signatures, QR/NFC check-in logs, and geographic location via geo-fencing (GPS).",
                },
              ],
            },
            {
              title: "Financial & Payroll Data",
              rows: [
                {
                  label: "Includes",
                  value:
                    "Basic salary, allowances, bonuses, tax components, bank account details, and Provident Fund contributions.",
                },
              ],
            },
            {
              title: "Sensitive Personal Documents",
              rows: [
                {
                  label: "Includes",
                  value:
                    "Digital copies of contracts, National IDs (NID), tax forms, and certifications.",
                },
              ],
            },
            {
              title: "Performance & Disciplinary Data",
              rows: [
                {
                  label: "Includes",
                  value:
                    "Warning letters, issue types, and rehire eligibility status.",
                },
              ],
            },
          ]}
        />
      </>
    ),
  },
  {
    id: "how-used",
    title: "How Information is Used",
    content: (
      <>
        <Paragraph>
          We process your data to provide a connected HR experience:
        </Paragraph>
        <BulletList
          items={[
            <>
              <Strong>Automation:</Strong> Calculating salary and leave balances
              based on real-time attendance data.
            </>,
            <>
              <Strong>Verification:</Strong> Using AI facial recognition and
              Wi-Fi locks to verify identity and presence in approved office
              areas.
            </>,
            <>
              <Strong>Integration:</Strong> Syncing approved leaves with
              attendance logs and payroll deduction logic.
            </>,
            <>
              <Strong>Reporting:</Strong> Generating utilization metrics, tax
              compliance reports, and organizational charts.
            </>,
          ]}
        />
      </>
    ),
  },
  {
    id: "sharing",
    title: "Data Sharing & Access Control",
    content: (
      <>
        <Paragraph>
          HRx utilizes Role-Based Access Control (RBAC) to ensure data privacy:
        </Paragraph>
        <DataBlocks
          items={[
            {
              title: "Payroll & Financials",
              rows: [
                {
                  label: "Access",
                  value: "Restricted to authorized HR and Finance roles only.",
                },
              ],
            },
            {
              title: "Disciplinary Records",
              rows: [
                {
                  label: "Access",
                  value: "Visible only to authorized HR/Admin roles.",
                },
              ],
            },
            {
              title: "Bank Integration",
              rows: [
                {
                  label: "Access",
                  value: "Shared via secure API for direct payment processing.",
                },
              ],
            },
            {
              title: "Attendance Logs",
              rows: [
                {
                  label: "Access",
                  value:
                    "Visible to the individual employee and their direct supervisor.",
                },
              ],
            },
          ]}
        />
      </>
    ),
  },
  {
    id: "security",
    title: "Data Security and Integrity",
    content: (
      <>
        <Paragraph>
          We implement industry-standard measures to protect your information:
        </Paragraph>
        <BulletList
          items={[
            <>
              <Strong>Encryption:</Strong> Sensitive data, including payroll
              information, is encrypted and masked for unauthorized users.
            </>,
            <>
              <Strong>Immutable Metadata:</Strong> Core identifiers (e.g.,
              Employee ID, Joining Date) are locked post-approval to prevent
              unauthorized edits.
            </>,
            <>
              <Strong>Audit Logs:</Strong> Every change to payroll or employee
              profiles is recorded with a timestamp and user ID for
              accountability.
            </>,
          ]}
        />
      </>
    ),
  },
  {
    id: "rights",
    title: "Employee Self-Service & Rights",
    content: (
      <>
        <Paragraph>
          Employees are empowered to manage their own data through the HRx
          portal:
        </Paragraph>
        <BulletList
          items={[
            <>
              <Strong>Access & Correction:</Strong> Employees can view their
              leave balances, payslips, and attendance patterns.
            </>,
            <>
              <Strong>Updates:</Strong> Employees may update editable fields
              such as emergency contacts and bank information.
            </>,
            <>
              <Strong>Notifications:</Strong> Real-time alerts for check-in
              confirmations, salary credits, and document expiry.
            </>,
          ]}
        />
      </>
    ),
  },
  {
    id: "retention",
    title: "Data Retention",
    content: (
      <Paragraph>
        HRx maintains historical records of the employment lifecycle. Profile
        data remains stored within the system to support audit trails and future
        hiring restrictions even after an employee leaves the organization.
      </Paragraph>
    ),
  },
  {
    id: "contact",
    title: "Contact Us",
    content: (
      <Paragraph>
        If you have any questions about this Privacy Policy or your data, please
        contact your organization's HR department or the HRx support team
        through our <TextLink href={CONTACT_PATH}>contact page</TextLink>.
      </Paragraph>
    ),
  },
];

const PrivacyPolicy = () => (
  <LegalPage
    badge={{ icon: ShieldCheck, label: "Privacy" }}
    title="Privacy Policy"
    description={`How ${COMPANY.name} collects, uses, and safeguards the personal and professional data processed within HRx.`}
    dateLabel="Last updated"
    date="Apr 30, 2026"
    sections={sections}
  />
);

export default PrivacyPolicy;
